import React from "react";

import type { VisitDisplay } from "../types";
import {
  collectSelectableIdsFromVisits,
  getCheckboxAggregateState,
  getSelectableVisitIds,
} from "../utils/visitBulkSelectionUtils";

export interface UseVisitBulkSelectionResult {
  selectedVisitIds: ReadonlySet<string>;
  clearSelection: () => void;
  removeVisitIds: (ids: Iterable<string>) => void;
  toggleVisitRow: (visit: VisitDisplay) => void;
  toggleGroup: (groupVisits: VisitDisplay[]) => void;
  visitRowCheckboxState: (visit: VisitDisplay) => {
    checked: boolean;
    indeterminate: boolean;
  };
  groupHeaderCheckboxState: (groupVisits: VisitDisplay[]) => {
    checked: boolean;
    indeterminate: boolean;
  };
  selectedCount: number;
}

export function useVisitBulkSelection(visits: VisitDisplay[]): UseVisitBulkSelectionResult {
  const [selectedVisitIds, setSelectedVisitIds] = React.useState<Set<string>>(() => new Set());

  const validIdSet = React.useMemo(() => new Set(collectSelectableIdsFromVisits(visits)), [visits]);

  React.useEffect(() => {
    setSelectedVisitIds((prev) => {
      let changed = false;
      const next = new Set<string>();
      for (const id of prev) {
        if (validIdSet.has(id)) next.add(id);
        else changed = true;
      }
      if (!changed && next.size === prev.size) return prev;
      return next;
    });
  }, [validIdSet]);

  const clearSelection = React.useCallback(() => {
    setSelectedVisitIds((prev) => (prev.size === 0 ? prev : new Set()));
  }, []);

  const removeVisitIds = React.useCallback((ids: Iterable<string>) => {
    setSelectedVisitIds((prev) => {
      const next = new Set(prev);
      let changed = false;
      for (const id of ids) {
        if (next.delete(id)) changed = true;
      }
      if (!changed) return prev;
      return next;
    });
  }, []);

  const toggleVisitRow = React.useCallback((visit: VisitDisplay) => {
    const ids = getSelectableVisitIds(visit);
    setSelectedVisitIds((prev) => {
      const { checked } = getCheckboxAggregateState(prev, ids);
      const next = new Set(prev);
      if (checked) {
        for (const id of ids) next.delete(id);
      } else {
        for (const id of ids) next.add(id);
      }
      return next;
    });
  }, []);

  const toggleGroup = React.useCallback((groupVisits: VisitDisplay[]) => {
    const ids = collectSelectableIdsFromVisits(groupVisits);
    setSelectedVisitIds((prev) => {
      const { checked } = getCheckboxAggregateState(prev, ids);
      const next = new Set(prev);
      if (checked) {
        for (const id of ids) next.delete(id);
      } else {
        for (const id of ids) next.add(id);
      }
      return next;
    });
  }, []);

  const visitRowCheckboxState = React.useCallback(
    (visit: VisitDisplay) =>
      getCheckboxAggregateState(selectedVisitIds, getSelectableVisitIds(visit)),
    [selectedVisitIds]
  );

  const groupHeaderCheckboxState = React.useCallback(
    (groupVisits: VisitDisplay[]) =>
      getCheckboxAggregateState(selectedVisitIds, collectSelectableIdsFromVisits(groupVisits)),
    [selectedVisitIds]
  );

  return {
    selectedVisitIds,
    clearSelection,
    removeVisitIds,
    toggleVisitRow,
    toggleGroup,
    visitRowCheckboxState,
    groupHeaderCheckboxState,
    selectedCount: selectedVisitIds.size,
  };
}
